import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

// Helpers
import { and } from "@/Common/helpers/sonar";
import { getSections, getSectionsAvailable, getAllSectionsEnabled, checkSectionsToDisable } from "@PayPlans/helpers/sections";

// Component Defaults
import {
	RetentionPayDefault,
	HolidayPayDefault,
	NightShiftPayDefault,
	LongevityDefault,
	FlaggedHoursBonusDefault,
	TopPerformanceBonusDefault,
	TargetedPerformanceBonusDefault,
} from "@PayPlans/helpers/DefaultSections";

// Contexts
import { usePayPlan } from "@PayPlans/context/PayPlans";

// Hooks
import { useLocation } from "@Common/hooks/Location/useLocation";

// Components
import SectionContainer from "@Layouts/components/SectionContainer";

// Custom Sections
import {
	NightShiftPay,
	HolidayPay,
	RetentionPay,
	DefaultPay,
	Longevity,
	TopTechniciansBonus,
	FlaggedHoursBonus,
	TargetedPerformanceBonus,
} from "./sections";

// MUI
import { Collapse, Grid } from "@mui/material";
import { TransitionGroup } from "react-transition-group";
import PayPlansLayout from "@/PayPlans/Layouts/PayPlansLayout";
import PdfPayPlanLayer from "@/PayPlans/views/Layers/Html/Sales/Associates";

const allSections = getSections(
	[
		"default",
		"nightShiftPay",
		"holidayPay",
		"retentionPay",
		"topTechniciansBonus",
		"longevity",
		"flaggedHoursBonus",
		"targetedPerformanceBonus",
	],
	[{ section: "topTechniciansBonus", modify: { title: "Top Technician(s) Bonus", description: "Note for top technicians bonus" } }]
);

// Constants
const contentAlignment = "flex-start";

const OPTIONAL_SECTIONS = new Map([
	["nightShiftPay", NightShiftPayDefault],
	["holidayPay", HolidayPayDefault],
	["retentionPay", RetentionPayDefault],
	["longevity", LongevityDefault],
	["flaggedHoursBonus", FlaggedHoursBonusDefault],
	["topTechniciansBonus", TopPerformanceBonusDefault],
	["targetedPerformanceBonus", TargetedPerformanceBonusDefault],
]);

const getIsSectionButtonDisabled = ({ isValid = false, isReadOnlyReason = false, isSomeNewPayPlan = false, loading = false }) =>
	!isValid || isReadOnlyReason || isSomeNewPayPlan || loading;

function hasIntegration(location, type) {
	return location?.services?.some((service) => service.type === type) ?? false;
}

const PayPlansModel = (props) => {
	const {
		data = null,
		isNew = false,
		loading = false,
		readOnly = false,
		updateModel = () => {},
		location = null,
		saving = false,
	} = props;
	const { isSomeNewPayPlan, isValid, selected, useSectionsErrors, position } = usePayPlan();
	const { errors, clearErrors, clearSectionErrors } = useSectionsErrors();

	const isReadOnly = !loading && (Boolean(data?.available) || position !== data?.position);

	// States
	const [isLazyLoading, setIsLazyLoading] = useState(true);
	const [sections, setSections] = useState(new Map());
	const [disabledSections, setDisabledSections] = useState([]);

	// Sections controls
	const handleAddSection = (key) => {
		const sectionFn = OPTIONAL_SECTIONS.get(key);
		if (!sectionFn) {
			return;
		}

		data[key] = sectionFn();
		setSections(getSectionsAvailable(allSections, data));
	};

	const handleDeleteSection = (section) => {
		const remove = allSections.get(section);

		// Remove section
		data[section] = null;
		if (remove?.type === "list") {
			data[section] = [];
		}

		clearSectionErrors(section);
		setSections(getSectionsAvailable(allSections, data));
		updateModel({ section, data: data[section] });
	};

	const handleUpdateSection = (...params) => {
		const [section, , value = null, isValidUpdate = true] = params;
		switch (section) {
			case "default":
			case "flaggedHoursBonus":
			case "nightShiftPay":
			case "holidayPay":
			case "retentionPay":
			case "longevity":
			case "topTechniciansBonus":
			case "targetedPerformanceBonus":
				data[section] = value;
				break;
			default:
				break;
		}
		// Update model
		Boolean(isValidUpdate) && updateModel({ section, data: data[section] });
	};

	const isDOMLoading = useMemo(() => Boolean(isLazyLoading || loading), [isLazyLoading, loading]);

	useMemo(() => {
		setIsLazyLoading(true);
		if (selected) {
			const milliseconds = 500;
			setTimeout(() => {
				setIsLazyLoading(false);
			}, milliseconds);
		}
	}, [selected, setIsLazyLoading]);

	useMemo(() => {
		setSections(getSectionsAvailable(allSections, data));
	}, [data]);

	useEffect(() => {
		return () => {
			clearErrors();
		};
	}, [clearErrors]);

	useEffect(() => {
		const hasGPSIntegration = hasIntegration(location, "GPS");
		if (!hasGPSIntegration) {
			setDisabledSections([...disabledSections, "targetedPerformanceBonus"]);
		} else {
			const disabled = disabledSections.filter((section) => section !== "targetedPerformanceBonus");
			setDisabledSections(disabled);
		}
	}, [location]);

	const isReadOnlyReason = readOnly || isReadOnly || saving;

	return (
		<PayPlansLayout
			htmlComponent={<PdfPayPlanLayer payPlan={data} selected={selected} loading={loading} />}
			data={data}
			allSections={allSections}
			sections={sections}
			disabledSections={disabledSections}
			disabled={getIsSectionButtonDisabled({
				isValid,
				isReadOnlyReason,
				isSomeNewPayPlan,
				loading,
			})}
			isNew={isNew}
			isValid={isValid}
			loading={loading}
			readOnly={isReadOnlyReason}
			onActionAddSection={handleAddSection}
		>
			{sections.get("flaggedHoursBonus") && (
				<TransitionGroup component={Grid} container item xs={12}>
					<Collapse sx={{ width: 1, marginBottom: 1 }}>
						<SectionContainer
							sectionId={"flaggedHoursBonus"}
							title={sections.get("flaggedHoursBonus").title}
							collapsible
							deletable
							loading={loading}
							readOnly={isReadOnlyReason}
							onDeleteAction={handleDeleteSection}
						>
							<FlaggedHoursBonus
								sectionId={"flaggedHoursBonus"}
								content={data?.flaggedHoursBonus}
								useSectionsErrors={useSectionsErrors}
								loading={isDOMLoading}
								readOnly={isReadOnlyReason}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				</TransitionGroup>
			)}

			<Grid container item xs={12} columnSpacing={2} sx={!(loading || isDOMLoading) ? { paddingTop: "0px !important" } : null}>
				<TransitionGroup
					component={Grid}
					container
					item
					xs={6}
					alignContent={contentAlignment}
					alignItems={contentAlignment}
					sx={{ marginTop: loading || isDOMLoading ? 0 : 1, marginBottom: 1 }}
				>
					<Collapse sx={{ width: 1 }}>
						<SectionContainer
							sectionId={"default"}
							title={"Default Flag Rate"}
							collapsible
							loading={loading}
							readOnly={isReadOnlyReason}
						>
							<DefaultPay
								sectionId={"default"}
								content={data?.default}
								errors={errors?.get("default")}
								loading={isDOMLoading}
								readOnly={isReadOnlyReason}
								onActionUpdate={handleUpdateSection}
								useSectionsErrors={useSectionsErrors}
								position={position}
							/>
						</SectionContainer>
					</Collapse>
					{and(Boolean(sections.get("nightShiftPay")), !isDOMLoading) && (
						<Collapse sx={{ width: 1, marginTop: 2 }}>
							<SectionContainer
								sectionId={"nightShiftPay"}
								title={sections.get("nightShiftPay").title}
								collapsible
								deletable
								loading={loading}
								readOnly={isReadOnlyReason}
								onDeleteAction={handleDeleteSection}
							>
								<NightShiftPay
									sectionId={"nightShiftPay"}
									content={data?.nightShiftPay}
									useSectionsErrors={useSectionsErrors}
									loading={loading}
									readOnly={isReadOnlyReason}
									onActionUpdate={handleUpdateSection}
								/>
							</SectionContainer>
						</Collapse>
					)}
					{and(Boolean(sections.get("longevity")), !isDOMLoading) && (
						<Collapse sx={{ width: 1, marginTop: 2 }}>
							<SectionContainer
								sectionId={"longevity"}
								title={sections.get("longevity").title}
								collapsible
								deletable
								loading={loading}
								readOnly={isReadOnlyReason}
								onDeleteAction={handleDeleteSection}
							>
								<Longevity
									content={data?.longevity}
									useSectionsErrors={useSectionsErrors}
									loading={loading}
									readOnly={isReadOnlyReason}
									onActionUpdate={handleUpdateSection}
								/>
							</SectionContainer>
						</Collapse>
					)}
				</TransitionGroup>
				<TransitionGroup
					component={Grid}
					container
					item
					xs={6}
					alignContent={contentAlignment}
					alignItems={contentAlignment}
					sx={{ marginTop: 1 }}
				>
					{and(Boolean(sections.get("holidayPay")), !isDOMLoading) && (
						<Collapse sx={{ width: 1, marginBottom: 2 }}>
							<SectionContainer
								sectionId={"holidayPay"}
								title={sections.get("holidayPay").title}
								collapsible
								deletable
								loading={loading}
								readOnly={isReadOnlyReason}
								onDeleteAction={handleDeleteSection}
							>
								<HolidayPay
									content={data?.holidayPay}
									useSectionsErrors={useSectionsErrors}
									loading={loading}
									readOnly={isReadOnlyReason}
									onActionUpdate={handleUpdateSection}
								/>
							</SectionContainer>
						</Collapse>
					)}
					{and(Boolean(sections.get("retentionPay")), !isDOMLoading) && (
						<Collapse sx={{ width: 1, marginBottom: 2 }}>
							<SectionContainer
								sectionId={"retentionPay"}
								title={sections.get("retentionPay").title}
								collapsible
								deletable
								loading={loading}
								readOnly={isReadOnlyReason}
								onDeleteAction={handleDeleteSection}
							>
								<RetentionPay
									sectionId={"retentionPay"}
									content={data?.retentionPay}
									useSectionsErrors={useSectionsErrors}
									loading={loading}
									readOnly={isReadOnlyReason}
									onActionUpdate={handleUpdateSection}
									position={position}
								/>
							</SectionContainer>
						</Collapse>
					)}
					{and(Boolean(sections.get("topTechniciansBonus")), !isDOMLoading) && (
						<Collapse sx={{ width: 1, marginBottom: 2 }}>
							<SectionContainer
								sectionId={"topTechniciansBonus"}
								title={sections.get("topTechniciansBonus").title}
								collapsible
								deletable
								loading={loading}
								readOnly={isReadOnlyReason}
								onDeleteAction={handleDeleteSection}
							>
								<TopTechniciansBonus
									content={data?.topTechniciansBonus}
									useSectionsErrors={useSectionsErrors}
									loading={loading}
									readOnly={isReadOnlyReason}
									onActionUpdate={handleUpdateSection}
								/>
							</SectionContainer>
						</Collapse>
					)}
				</TransitionGroup>
				{and(Boolean(sections.get("targetedPerformanceBonus")), !isDOMLoading) && (
					<TransitionGroup component={Grid} container item xs={12}>
						<Collapse sx={{ width: 1, marginBottom: 1, marginTop: 1 }}>
							<SectionContainer
								sectionId={"targetedPerformanceBonus"}
								title={sections.get("targetedPerformanceBonus").title}
								collapsible
								deletable
								loading={loading}
								readOnly={isReadOnlyReason}
								onDeleteAction={handleDeleteSection}
							>
								<TargetedPerformanceBonus
									sectionId={"targetedPerformanceBonus"}
									content={data?.targetedPerformanceBonus}
									useSectionsErrors={useSectionsErrors}
									loading={isDOMLoading}
									readOnly={isReadOnlyReason}
									onActionUpdate={handleUpdateSection}
								/>
							</SectionContainer>
						</Collapse>
					</TransitionGroup>
				)}
			</Grid>
		</PayPlansLayout>
	);
};

PayPlansModel.propTypes = {
	data: PropTypes.object,
	isNew: PropTypes.bool,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	updateModel: PropTypes.func,
	location: PropTypes.object,
	saving: PropTypes.bool,
};

export default PayPlansModel;
